import React, { useState } from 'react';
import { Check, Info, KeyRound, Search } from 'lucide-react';
import styled from '../../styled';
import { COLORS } from '../../themes/color';

const VISIBLE_INTEGRATIONS = {
  Awin: { icon: '/images/icons/awin.png', label: 'Awin' },
  'CJ Affiliate': { icon: '/images/icons/cj.svg', label: 'CJ Affiliate' },
  Levanta: { icon: '/images/icons/levanta.png', label: 'Levanta' },
  Linkby: { icon: '/images/icons/linkby.svg', label: 'Linkby' },
  Skimlinks: { icon: '/images/icons/skimlinks.jpeg', label: 'Skimlinks' }
} as const;

type IntegrationName = keyof typeof VISIBLE_INTEGRATIONS;

type ChipItem =
  | { kind: 'integration'; name: IntegrationName }
  | { kind: 'skeleton'; width: string };

const INTEGRATION_ORDER = (
  Object.keys(VISIBLE_INTEGRATIONS) as IntegrationName[]
).sort((a, b) => a.localeCompare(b));

const SKELETON_WIDTHS = ['58px', '46px', '64px', '52px', '50px', '56px'];

/** Integrations zigzag left/right in alphabetical order with skeletons opposite. */
const CHIP_ITEMS: ChipItem[] = INTEGRATION_ORDER.reduce<ChipItem[]>(
  (items, name, index) => {
    const skeleton: ChipItem = {
      kind: 'skeleton',
      width: SKELETON_WIDTHS[index % SKELETON_WIDTHS.length]
    };
    const integration: ChipItem = { kind: 'integration', name };
    return items.concat(
      index % 2 === 0 ? [integration, skeleton] : [skeleton, integration]
    );
  },
  []
).concat([
  { kind: 'skeleton', width: '48px' },
  { kind: 'skeleton', width: '62px' }
]);

const Dialog = styled('div')`
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  overflow: hidden;
  border: 1px solid #d9dde3;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 18px 55px rgba(14, 28, 43, 0.12);
`;

const DialogGrid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 320px;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const IntegrationsPane = styled('div')`
  min-width: 0;
  padding: 20px 18px;
  border-right: 1px solid #d9dde3;
  background: #f7f7f7;

  @media (max-width: 680px) {
    border-right: 0;
    border-bottom: 1px solid #d9dde3;
  }
`;

const SetupPane = styled('div')`
  min-width: 0;
  padding: 20px 18px 18px;
  background: #fff;
`;

const PaneTitle = styled('div')`
  margin: 0 0 16px;
  color: #171717;
  font-size: 17px;
  font-weight: 700;
`;

const SearchBox = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  margin-bottom: 14px;
  padding: 0 12px;
  border: 1px solid #e0e3e7;
  border-radius: 8px;
  background: #fff;
  color: #9aa1aa;
  font-size: 12px;
`;

const ChipGrid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 8px;
  max-height: 196px;
  overflow: hidden;
  mask-image: linear-gradient(180deg, #000 72%, transparent 100%);
  -webkit-mask-image: linear-gradient(180deg, #000 72%, transparent 100%);
`;

const IntegrationChip = styled('button')<{ $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;
  padding: 4px 9px 4px 5px;
  border: 1px solid
    ${(p) => (p.$selected ? COLORS.PRIMARY : 'rgba(0, 0, 0, 0.04)')};
  border-radius: 999px;
  background: ${(p) => (p.$selected ? COLORS.PRIMARY : '#e9e9e9')};
  color: ${(p) => (p.$selected ? '#fff' : '#50555c')};
  font-size: 11px;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &:focus-visible {
    outline: 2px solid ${COLORS.PRIMARY};
    outline-offset: 2px;
  }
`;

const SkeletonChip = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;
  padding: 4px 9px 4px 5px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  border-radius: 999px;
  background: #ececec;
  overflow: hidden;
`;

const SkeletonDot = styled('span')`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #d8d8d8;
  flex-shrink: 0;
`;

const SkeletonBar = styled('span')<{ width: string }>`
  display: inline-block;
  height: 8px;
  border-radius: 100px;
  background: #d0d0d0;
  width: ${(p) => p.width};
`;

const ChipIcon = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 14px;
    height: 14px;
    object-fit: contain;
  }
`;

const SetupHeader = styled('div')`
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 14px;

  strong {
    color: #171717;
    font-size: 15px;
  }

  span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #458eb8;
    font-size: 10px;
  }
`;

const ConnectionCard = styled('div')`
  padding: 16px;
  border: 1px solid #e0e3e7;
  border-radius: 8px;
  background: #fff;
`;

const ConnectionTitle = styled('div')`
  margin-bottom: 14px;
  color: #242424;
  font-size: 12px;
`;

const Field = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 47px;
  margin-bottom: 10px;
  padding: 0 12px;
  border: 1px solid #dfe3e7;
  border-radius: 7px;
  color: #8c9198;
  font-size: 11px;

  svg {
    color: #8c9198;
  }
`;

const TestButton = styled('button')<{ $success: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  height: 38px;
  border: 0;
  border-radius: 7px;
  background: ${(p) => (p.$success ? '#52c41a' : COLORS.PRIMARY)};
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  box-shadow: 0 7px 18px rgba(46, 92, 255, 0.22);
  cursor: pointer;
  transition: background 0.2s ease;

  &:focus-visible {
    outline: 2px solid ${COLORS.PRIMARY};
    outline-offset: 2px;
  }
`;

export const IntegrationPipelineMockup = () => {
  const [connected, setConnected] = useState(false);

  return (
    <Dialog>
      <DialogGrid>
        <IntegrationsPane>
          <PaneTitle>Connect your affiliate data</PaneTitle>
          <SearchBox>
            <Search size={17} />
            Search integrations
          </SearchBox>
          <ChipGrid aria-label="Available affiliate integrations">
            {CHIP_ITEMS.map((item, index) => {
              if (item.kind === 'skeleton') {
                return (
                  <SkeletonChip key={`skeleton-${index}`} aria-hidden>
                    <SkeletonDot />
                    <SkeletonBar width={item.width} />
                  </SkeletonChip>
                );
              }

              const { icon, label } = VISIBLE_INTEGRATIONS[item.name];

              return (
                <IntegrationChip
                  key={label}
                  type="button"
                  $selected={item.name === 'Awin'}
                  aria-pressed={item.name === 'Awin'}
                >
                  <ChipIcon>
                    <img src={icon} alt="" />
                  </ChipIcon>
                  {label}
                </IntegrationChip>
              );
            })}
          </ChipGrid>
        </IntegrationsPane>
        <SetupPane>
          <SetupHeader>
            <strong>Connect Awin</strong>
            <span>
              <Info size={12} /> Show instructions
            </span>
          </SetupHeader>
          <ConnectionCard>
            <ConnectionTitle>Add another connection to Awin</ConnectionTitle>
            <Field>
              Publisher ID *
              <KeyRound size={13} />
            </Field>
            <Field>
              API Token *
              <KeyRound size={13} />
            </Field>
            <TestButton
              type="button"
              $success={connected}
              onClick={() => setConnected(true)}
            >
              {connected && <Check size={14} />}
              {connected ? 'Connection verified' : 'Test connection'}
            </TestButton>
          </ConnectionCard>
        </SetupPane>
      </DialogGrid>
    </Dialog>
  );
};
